import api, { getResponseError } from "../../services/api"
import {
  ICalculateInstallment,
  IOrder,
  IOrderCharge,
  PaymentData,
} from "../types/Order"
import { IErrorResponse, IResponse, TResponse } from "../types/ErrorResponse"
import { TSale, TSalePayment, TSaleResult } from "../types/Sale"
import { numberOnly } from "../../masks/masks"
import { TPaymentMethodEnum, TProductLinkAffiliated } from "../types/Product"
import { TCheckoutAccess, TCheckoutAccessResult } from "../types/Checkout"
import RequestCache from "../../services/RequestCache"

const create = async ({ data }: { data: TSale }): Promise<IResponse> => {
  try {
    const { data: response } = await api.post("/Order", setSendData(data))
    if (response) {
      return {
        success: true,
        code: 0,
        error: "",
        data: response,
      }
    }

    return {
      success: false,
      code: 1,
      error: "Erro inesperado do servidor",
    }
  } catch (error) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const getPaymentsData = (payment: TSalePayment): PaymentData[] => {
  return payment.payments.map((pay, index) => {
    const payData = { ...pay }

    if (index === 0) {
      payData.total = payment.total
      payData.discount = payment.discount
      payData.subtotal = payment.subtotal
      payData.voucherId = payment.voucherId
      payData.voucherIdentifier = payment.voucherIdentifier
      payData.voucherStatus = payment.voucherStatus
      payData.voucherType = payment.voucherType
      payData.voucherValue = payment.voucherValue
    }

    if (payData.creditCardData) {
      payData.creditCardData.number = numberOnly(payData.creditCardData.number)
      payData.creditCardData.name = payData.creditCardData.name.trim()
      payData.creditCardData.installments = payment.installment
    }
    if (payData.debitCardData) {
      payData.debitCardData.number = numberOnly(payData.debitCardData.number)
      payData.debitCardData.name = payData.debitCardData.name.trim()
    }
    return payData
  })
}

const setSendData = (data: TSale) => {
  const item: IOrder = {
    checkoutOrderId: data.checkoutOrderId,
    lostSaleCode: data.lostSaleCode,
    productPriceId: data.productPriceId,
    quantity: data.quantity,
    name: data.name.trim(),
    document: numberOnly(data.document),
    email: data.email.trim().toLowerCase(),
    phone: numberOnly(data.phone),
    affiliateCode: data.onlyProducer ? null : data.affiliateCode,
    src: data.src,
    sck: data.sck,
    utm_campaign: data.utm_campaign,
    utm_source: data.utm_source,
    utm_medium: data.utm_medium,
    utm_content: data.utm_content,
    utm_term: data.utm_term,
    checkoutModelId: data.checkoutModelId,
    kondutoId: data.kondutoId,
    onlyProducer: data.onlyProducer,
    pageAccessId: data.pageAccessId,
    sessionToken: data.sessionToken,
    clientIP: data.clientIP,
    recaptchaToken: data.recaptchaToken,
    isPurposeCardUpdate: data.isPurposeCardUpdate,
    isCardUpdateOnly: data.isCardUpdateOnly,
    subscriptionCodeId: data.subscriptionCodeId,
    customFields: data.customFields ? JSON.stringify(data.customFields) : null,
    address: {
      city: data.address.city.trim(),
      complement: data.address.complement?.trim(),
      neighborhood: data.address.neighborhood.trim(),
      number: data.address.number.trim(),
      reference: data.address.reference?.trim(),
      state: data.address.state.trim(),
      street: data.address.street.trim(),
      zipCode: numberOnly(data.address.zipCode),
    },
    paymentData: getPaymentsData(data.payment),
    /*paymentData: {
      paymentMethod: data.payment.methodType,
      discount: data.payment.discount,
      subtotal: data.payment.subtotal,
      total: data.payment.total,
      voucherStatus: data.payment.voucherStatus,
      voucherId: data.payment.voucherId,
      voucherIdentifier: data.payment.voucherIdentifier,
      voucherType: data.payment.voucherType,
      voucherValue: data.payment.voucherValue,
    },*/
  }

  /*if (data.payment.cardValidate && data.payment.cardValidate !== null) {
    if (data.payment.cardValidate.includes("/")) {
      const cardValidate = data.payment.cardValidate.split("/")
      data.payment.cardExpiresMonth = Number(cardValidate[0])
      data.payment.cardExpiresYear = Number(cardValidate[1])
    }
  }*/

  /*if (item.paymentData.paymentMethod === TPaymentMethodEnum.CreditCard) {
    item.paymentData.creditCardData = {
      number: numberOnly(data.payment.cardNumber),
      name: data.payment.cardName?.trim(),
      cvv: data.payment.cardCCV?.trim(),
      year: data.payment.cardExpiresYear ?? 0,
      month: data.payment.cardExpiresMonth ?? 0,
      installments: data.payment.installment,
    }
  }

  if (item.paymentData.paymentMethod === TPaymentMethodEnum.DebitCard) {
    item.paymentData.debitCardData = {
      number: numberOnly(data.payment.cardNumber),
      name: data.payment.cardName?.trim(),
      cvv: data.payment.cardCCV?.trim(),
      year: data.payment.cardExpiresYear ?? 0,
      month: data.payment.cardExpiresMonth ?? 0,
    }
  }*/

  if (data?.products) {
    item.orderBumps = data.products.map((x) => ({
      productPriceId: x.productPriceId,
      description: x.productName?.trim(),
      amount: x.amount,
      value: x.value,
    }))
  }

  return item
}

const verified = async ({
  chargeId,
}: {
  chargeId?: string
}): Promise<IOrderCharge | undefined> => {
  if (!chargeId) {
    return undefined
  }
  try {
    const { data: response } = await api.get(`/Order/charge/${chargeId}`)
    if (response) {
      return response
    }

    return undefined
  } catch (error: any) {
    return undefined
  }
}

const calculeIsntallments = async ({
  priceId,
  newValue,
}: {
  priceId: string
  newValue: number
}): Promise<ICalculateInstallment | IErrorResponse> => {
  const cache = new RequestCache(3)
  const params = { priceId, newValue }

  const cachedData = cache.get<ICalculateInstallment | IErrorResponse>(
    "calculateInstallments",
    params
  )
  if (cachedData) return cachedData

  try {
    const { data: response } = await api.get(
      `/Order/price/calculate/${priceId}`,
      { params: { NewValue: newValue } }
    )

    const result = response || { code: 1, error: "Erro inesperado do servidor" }

    cache.set("calculateInstallments", params, result)

    return result
  } catch (error: any) {
    const e = getResponseError(error)
    return {
      code: 2,
      error: e,
    }
  }
}

const getCheckoutOrder = async ({
  checkoutOrderCodeId,
  afid,
}: {
  checkoutOrderCodeId?: string
  afid?: string | null
}): Promise<TSaleResult | undefined> => {
  if (!checkoutOrderCodeId) {
    return undefined
  }
  try {
    const { data: response } = await api.get(
      `/Order/code/${checkoutOrderCodeId}?afid=${afid}`
    )
    if (response) {
      return response
    }

    return undefined
  } catch (error: any) {
    return undefined
  }
}

const getCheckoutSubscription = async ({
  subscriptionCodeId,
}: {
  subscriptionCodeId?: string
}): Promise<TSaleResult | undefined> => {
  if (!subscriptionCodeId) {
    return undefined
  }
  try {
    const { data: response } = await api.get(
      `/Subscription/code/${subscriptionCodeId}`
    )
    if (response) {
      return response
    }

    return undefined
  } catch (error: any) {
    return undefined
  }
}

const getCheckoutOrderTest = async ({
  checkoutOrderCodeId,
  afid,
}: {
  checkoutOrderCodeId?: string
  afid?: string | null
}): Promise<TSaleResult | undefined> => {
  if (!checkoutOrderCodeId) {
    return undefined
  }
  try {
    const { data: response } = await api.get(
      `/Order/code/${checkoutOrderCodeId}/test?afid=${afid}`
    )
    if (response) {
      return response
    }

    return undefined
  } catch (error: any) {
    return undefined
  }
}

const getProductLinkAffiliated = async ({
  affiliatedCodeId,
  ap,
  clientIp,
  userAgent,
  browser,
  language,
  pageUrl,
}: {
  affiliatedCodeId?: string
  ap?: string | null
  clientIp?: string | null
  userAgent?: string | null
  browser?: string | null
  language?: string | null
  pageUrl?: string | null
}): Promise<TProductLinkAffiliated | null> => {
  if (!affiliatedCodeId) {
    return null
  }
  try {
    const url = `/Order/link/affiliated/${affiliatedCodeId}?ap=${ap ?? ""}&ci=${
      clientIp ?? ""
    }&ua=${userAgent ?? ""}&br=${browser ?? ""}&la=${language ?? ""}&pu=${
      pageUrl ?? ""
    }`
    const { data: response } = await api.get<TProductLinkAffiliated>(url)
    if (response) {
      return response
    }

    return null
  } catch (error: any) {
    return null
  }
}

const registerAccess = async ({
  data,
}: {
  data: TCheckoutAccess
}): Promise<TResponse<TCheckoutAccessResult>> => {
  try {
    // validação para obter o token na hora de registrar o acesso a página do checkout
    // antigamente devolvia apenas o id como string
    // quando foi feito não houve mais a necessidade da implementação da parte do backend
    // futuramente quando o back for implementado remover essa parte da string
    const { data: response } = await api.post<string | TCheckoutAccessResult>(
      "Order/register/access",
      data
    )
    if (response) {
      if (typeof response === "string") {
        return {
          success: true,
          code: 0,
          error: "",
          data: {
            id: response,
            token: undefined,
          },
        }
      }
      return {
        success: true,
        code: 0,
        error: "",
        data: response,
      }
    }

    return {
      success: false,
      code: 1,
      error: "Erro inesperado do servidor",
    }
  } catch (error: any) {
    const e = getResponseError(error)
    return {
      success: false,
      code: 2,
      error: e,
    }
  }
}

const OrderController = {
  create,
  verified,
  calculeIsntallments,
  getCheckoutOrder,
  getCheckoutOrderTest,
  getProductLinkAffiliated,
  registerAccess,
  getCheckoutSubscription,
}

export default OrderController
